import React, {FC} from 'react';
import {useTranslation} from 'react-i18next';
import {useLocation} from 'react-router-dom';

import featureEditorImg from 'assets/v3/video-translator/feature-editor.webp';
import featureLocalizationImg from 'assets/v3/video-translator/feature-localization.webp';
import featureMultilangImg from 'assets/v3/video-translator/feature-multilang.webp';
import heroImg from 'assets/v3/video-translator/hero.webp';

import {JsonLd, buildProductJsonLd, PageCta, PageHero, PageShell, PageTitle, Section} from 'components';
import Icon from 'components/v3/Icon';
import {btnPrimaryLg, btnSecondary} from 'components/v3/classes';

const GLOBE_PATHS = [
  'M12 21a9.004 9.004 0 0 0 8.716-6.747M12 21a9.004 9.004 0 0 1-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 0 1 7.843 4.582M12 3a8.997 8.997 0 0 0-7.843 4.582m15.686 0A11.953 11.953 0 0 1 12 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0 1 21 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0 1 12 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 0 1 3 12c0-1.605.42-3.113 1.157-4.418',
];
const DOCUMENT_PATHS = [
  'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
];
const PENCIL_SQUARE_PATHS = [
  'M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10',
];

interface Feature {
  descKey: string;
  iconPaths: string[];
  image: string;
  /** 文右图左（false=图左文右） */
  reversed?: boolean;
  titleKey: string;
}

interface UseCase {
  descKey: string;
  titleKey: string;
}

const renderUseCase = (useCase: UseCase, t: (key: string) => string) => (
  <div className="glass-card rounded-2xl p-6" key={useCase.titleKey}>
    <div className="h-1.5 w-10 rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500 mb-5" />
    <h3 className="text-base font-semibold text-slate-900 mb-2">{t(useCase.titleKey)}</h3>
    <p className="text-sm text-slate-500 leading-relaxed">{t(useCase.descKey)}</p>
  </div>
);

const VideoTranslator: FC = () => {
  const {t} = useTranslation('products');
  const {search} = useLocation();

  // 与首页一致：透传 utm_source 到视频翻译控制台
  const utmSource = new URLSearchParams(search).get('utm_source');
  // 新 workspace（2026-09-16，原 /video），透传 utm_source
  const videoUrl = `https://www.xiangjifanyi.com/workspace/video-subtitle-translator${
    utmSource ? `?utm_source=${utmSource}` : ''
  }`;

  const features: Feature[] = [
    {
      descKey: 'video.features.multilang.desc',
      iconPaths: GLOBE_PATHS,
      image: featureMultilangImg,
      titleKey: 'video.features.multilang.title',
    },
    {
      descKey: 'video.features.localization.desc',
      iconPaths: DOCUMENT_PATHS,
      image: featureLocalizationImg,
      reversed: true,
      titleKey: 'video.features.localization.title',
    },
    {
      descKey: 'video.features.editor.desc',
      iconPaths: PENCIL_SQUARE_PATHS,
      image: featureEditorImg,
      titleKey: 'video.features.editor.title',
    },
  ];

  const useCases: UseCase[] = [
    {descKey: 'video.useCases.shortVideoAd.desc', titleKey: 'video.useCases.shortVideoAd.title'},
    {descKey: 'video.useCases.socialContent.desc', titleKey: 'video.useCases.socialContent.title'},
    {descKey: 'video.useCases.productDemo.desc', titleKey: 'video.useCases.productDemo.title'},
    {descKey: 'video.useCases.socialRemix.desc', titleKey: 'video.useCases.socialRemix.title'},
  ];

  return (
    <PageShell>
      <PageTitle
        canonicalUrl="https://www.xiangjifanyi.com/home/video-translator"
        description={t('video.descLine1')}
        title={t('video.title')}
      />
      <JsonLd
        data={buildProductJsonLd({
          homeLabel: t('common:nav.homeAria'),
          path: '/video-translator',
          productDescription: t('video.descLine1'),
          productName: t('video.title'),
        })}
      />
      <PageHero
        actions={
          <a className={btnPrimaryLg} href={videoUrl} rel="noreferrer" target="_blank">
            {t('video.cta')}
          </a>
        }
        aside={
          <img
            alt={t('video.title')}
            className="w-full max-w-xl mx-auto rounded-3xl shadow-2xl"
            decoding="async"
            src={heroImg}
          />
        }
        badge={t('video.hero.badge')}
        description={
          <>
            {t('video.descLine1')}
            <br />
            {t('video.descLine2')}
          </>
        }
        title={t('video.title')}
        variant="light"
      />
      <Section bg="muted" title={t('video.whyTitle')}>
        <div className="space-y-16 lg:space-y-24">
          {features.map((feature) => (
            <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center" key={feature.titleKey}>
              <img
                alt={t(feature.titleKey)}
                className={`w-full rounded-2xl shadow-xl ${feature.reversed ? 'lg:order-2' : ''}`}
                decoding="async"
                loading="lazy"
                src={feature.image}
              />
              <div className={feature.reversed ? 'lg:order-1' : ''}>
                <div className="w-12 h-12 rounded-lg bg-violet-50 flex items-center justify-center mb-5">
                  <Icon className="w-6 h-6 text-violet-600" paths={feature.iconPaths} />
                </div>
                <h3 className="font-display font-bold text-2xl lg:text-3xl text-slate-900 tracking-tight mb-4">
                  {t(feature.titleKey)}
                </h3>
                <p className="text-slate-500 lg:text-lg leading-relaxed mb-7">{t(feature.descKey)}</p>
                <a className={btnSecondary} href={videoUrl} rel="noreferrer" target="_blank">
                  {t('video.cta')}
                </a>
              </div>
            </div>
          ))}
        </div>
      </Section>
      <Section title={t('video.useCasesTitle')}>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">{useCases.map((u) => renderUseCase(u, t))}</div>
      </Section>
      <PageCta
        actions={
          <a className={btnPrimaryLg} href={videoUrl} rel="noreferrer" target="_blank">
            {t('video.cta')}
          </a>
        }
        description={t('video.descLine2')}
        title={t('video.ctaTitle')}
      />
    </PageShell>
  );
};

export default VideoTranslator;
